/**
 * FullVSL embed postMessage bridge (FUL-107 / FUL-95 findings IFRAME-7/3/9/10).
 *
 * The player is the primary surface inside a Full Funnel / HighLevel iframe. In
 * that context several things that a same-origin embed could do are impossible
 * or blocked: analytics/CTA/resume events are trapped in the iframe (IFRAME-7),
 * `window.open` needs `allow-popups` (IFRAME-3), fullscreen needs
 * `allowfullscreen` (IFRAME-9), and host-page DOM / `CustomEvent`s cannot cross
 * the boundary (IFRAME-10). This module is the single, defensive channel the
 * player uses to ask the host page to do those things on its behalf.
 *
 * Contract surface (additive over `EmbedPostMessage`, contracts §10): every
 * outbound message carries `source: "fullvsl"` and a discriminating `kind`. The
 * host scopes its listener on `source === "fullvsl"` and switches on `kind`:
 *
 *   - "event"            → { source, kind, vslId, event: PlayerEvent }   (IFRAME-7)
 *   - "navigate"         → { source, kind, vslId, url, destinationType } (IFRAME-3)
 *   - "reveal"           → { source, kind, vslId, selector }             (IFRAME-10)
 *   - "custom_event"     → { source, kind, vslId, name, detail }         (IFRAME-10)
 *   - "fullscreen"       → { source, kind, vslId, action: "enter"|"exit" } (IFRAME-9)
 *
 * Hardening rules baked in (PRD §11.1 + FUL-95 findings):
 *   - Only acts when actually embedded (`window.parent !== window`).
 *   - postMessage targetOrigin is the configured allow-list when a single
 *     non-wildcard origin is supplied, otherwise `"*"` (the host already knows it
 *     embedded us; restricting the receiver origin is the host's job). Hosts that
 *     want strict origin hygiene pass one `allowedParentOrigins` entry.
 *   - Never throws: every browser API is guarded so the player keeps working
 *     whether or not a host listener is present.
 *
 * No PII is sent over this channel beyond what `PlayerEvent` already carries
 * (visitor/session ids required for analytics de-dup).
 */

import type { ID, PlayerEvent } from "../../contracts/src/contracts";

/** Base fields shared by every outbound embed message. */
export interface EmbedBridgeMessageBase {
  source: "fullvsl";
  /** Discriminator the host switches on; mirrors the message purpose. */
  kind: "event" | "navigate" | "reveal" | "custom_event" | "fullscreen";
  vslId: ID;
}

export interface EmbedBridgeEventMessage extends EmbedBridgeMessageBase {
  kind: "event";
  /** Normalized player event (FUL-5 vocabulary). */
  event: PlayerEvent;
}

export interface EmbedBridgeNavigateMessage extends EmbedBridgeMessageBase {
  kind: "navigate";
  /** Absolute URL the host should open / route to. */
  url: string;
  /** CTA destination type so the host can pick the right surface. */
  destinationType: string;
}

export interface EmbedBridgeRevealMessage extends EmbedBridgeMessageBase {
  kind: "reveal";
  /** CSS selector the host should reveal on its own document. */
  selector: string;
}

export interface EmbedBridgeCustomEventMessage extends EmbedBridgeMessageBase {
  kind: "custom_event";
  /** `js_event` CustomEvent name the host should re-dispatch on its document. */
  name: string;
  /** Opaque detail payload (the CTA destination config). */
  detail: unknown;
}

export interface EmbedBridgeFullscreenMessage extends EmbedBridgeMessageBase {
  kind: "fullscreen";
  action: "enter" | "exit";
}

export type EmbedBridgeMessage =
  | EmbedBridgeEventMessage
  | EmbedBridgeNavigateMessage
  | EmbedBridgeRevealMessage
  | EmbedBridgeCustomEventMessage
  | EmbedBridgeFullscreenMessage;

export interface EmbedBridgeOptions {
  /** Stable VSL id stamped on every message. */
  vslId: ID;
  /**
   * Allow-list of parent origins. When exactly one non-`"*"` origin is supplied
   * it is used as the postMessage `targetOrigin` (recommended). Otherwise the
   * bridge sends with `"*"` so any embedding host still receives the message.
   * Default: `["*"]` (permissive — restrict in production via the install API).
   */
  allowedParentOrigins?: string[];
}

/** True when the player is actually embedded in another window. */
export function isEmbedded(): boolean {
  try {
    return typeof window !== "undefined" && window.parent !== window;
  } catch {
    return false;
  }
}

/**
 * Resolve the postMessage targetOrigin from the allow-list. A single explicit
 * origin is used verbatim; anything else (empty, wildcard, or multiple) falls
 * back to `"*"` since the bridge cannot know which allowed origin is the parent.
 */
export function resolveTargetOrigin(allowed?: string[]): string {
  if (allowed && allowed.length === 1 && allowed[0] && allowed[0] !== "*") {
    return allowed[0];
  }
  return "*";
}

/**
 * Defensive postMessage bridge to the embedding host. No-ops when not embedded,
 * when `window.parent` is inaccessible (cross-origin without handles), or when
 * `postMessage` is unavailable. Never throws.
 */
export class EmbedBridge {
  private readonly parent: Window | null;
  private readonly targetOrigin: string;
  private readonly vslId: ID;
  private readonly embedded: boolean;

  constructor(opts: EmbedBridgeOptions) {
    this.vslId = opts.vslId;
    this.embedded = isEmbedded();
    this.targetOrigin = resolveTargetOrigin(opts.allowedParentOrigins);
    this.parent = this.embedded ? safeParent() : null;
  }

  /** Whether the bridge will actually deliver messages (i.e. we are embedded). */
  get active(): boolean {
    return this.embedded && this.parent != null;
  }

  /** Send a normalized player event to the host (IFRAME-7). */
  sendEvent(event: PlayerEvent): void {
    this.send({ source: "fullvsl", kind: "event", vslId: this.vslId, event });
  }

  /** Ask the host to open / route to a destination URL (IFRAME-3 fallback). */
  sendNavigate(url: string, destinationType: string): void {
    if (!url) return;
    this.send({
      source: "fullvsl",
      kind: "navigate",
      vslId: this.vslId,
      url,
      destinationType,
    });
  }

  /** Ask the host to reveal a delayed page element by selector (IFRAME-10). */
  sendReveal(selector: string): void {
    if (!selector) return;
    this.send({ source: "fullvsl", kind: "reveal", vslId: this.vslId, selector });
  }

  /** Ask the host to re-dispatch a `js_event` CustomEvent (IFRAME-10). */
  sendCustomEvent(name: string, detail: unknown): void {
    if (!name) return;
    this.send({ source: "fullvsl", kind: "custom_event", vslId: this.vslId, name, detail });
  }

  /** Ask the host to enter/exit fullscreen on the embed iframe (IFRAME-9). */
  sendFullscreen(action: "enter" | "exit"): void {
    this.send({ source: "fullvsl", kind: "fullscreen", vslId: this.vslId, action });
  }

  private send(msg: EmbedBridgeMessage): void {
    if (!this.active) return;
    const target = this.parent;
    if (!target) return;
    try {
      target.postMessage(msg, this.targetOrigin);
    } catch {
      // postMessage is best-effort; a thrown SecurityError must not break playback.
    }
  }
}

/** Resolve `window.parent` without throwing on cross-origin access. */
function safeParent(): Window | null {
  try {
    return window.parent ?? null;
  } catch {
    return null;
  }
}
